import React from 'react'
import { pdf } from '@react-pdf/renderer'
import { getData } from '../../../api/indexedDBHandler'
import PairCountTournamentProtocolPDF from './PairCountTournamentProtocolPDF'
import { getLogoUrl } from '../../../api/Logo'
import { dateNowProtocol } from '../../../api/formatDate'

const calculateRankings = (pairs) => {
  // 1. Фильтруем только зачётную рыбу (weight > 0) и недисквалифицированные команды
  const validPairs = pairs
    .map((pair) => ({
      ...pair,
      validFishCount: pair.fishList.filter((fish) => fish.weight > 0).length,
      totalValidWeight: pair.fishList.reduce(
        (sum, fish) => (fish.weight > 0 ? sum + fish.weight : sum),
        0
      ),
    }))
    .filter((pair) => !pair.disqualified)
  console.log('validPairs', validPairs)
  // 2. Сортируем по количеству зачётной рыбы (по убыванию), затем по общему весу (по убыванию)
  const sortedPairs = [...validPairs].sort((a, b) => {
    if (b.validFishCount !== a.validFishCount) {
      return b.validFishCount - a.validFishCount
    }
    return b.totalValidWeight - a.totalValidWeight
  })

  // 3. Присваиваем места с учетом ничьих
  let rankedPairs = []
  let currentRank = 1

  while (currentRank <= sortedPairs.length) {
    const currentCount = sortedPairs[currentRank - 1].validFishCount
    const sameRankPairs = sortedPairs.filter(
      (p) => p.validFishCount === currentCount
    )

    if (sameRankPairs.length > 1) {
      // Вычисляем средний ранг для ничьих
      const averageRank = currentRank + (sameRankPairs.length - 1) / 2

      rankedPairs.push(
        ...sameRankPairs.map((pair) => ({
          ...pair,
          overallRank: averageRank,
          isTie: sameRankPairs.length > 1,
        }))
      )
      currentRank += sameRankPairs.length
    } else {
      rankedPairs.push({
        ...sortedPairs[currentRank - 1],
        overallRank: currentRank,
        isTie: false,
      })
      currentRank++
    }
  }

  // 4. Добавляем дисквалифицированные команды в конец
  const disqualifiedPairs = pairs
    .filter((pair) => pair.disqualified)
    .map((pair) => ({
      ...pair,
      overallRank: pairs.length + 3, // Место после всех
      validFishCount: 0,
      totalValidWeight: 0,
      isTie: false,
    }))

  return [...rankedPairs, ...disqualifiedPairs]
}

const generateProtocolData = async (id) => {
  const resultsU = await getData('teams', 'id', id)
  if (resultsU.length === 0) throw new Error('No team data found')

  const { value: teamValue } = resultsU[0]
  const filteredTeams = Object.values(teamValue).filter(
    (teamEntry) =>
      teamEntry?.team?.status === 'Заявка одобрена' &&
      teamEntry?.team?.status_location === 1
  )

  const resultsS = await getData('stage', 'id', id)
  if (resultsS.length === 0) throw new Error('No stage data found')

  const { value: stageValue } = resultsS[0]
  // const stageArray = Object.values(stageValue).filter(Boolean)
  const stageArray = Object.values(stageValue).filter(
    (teamEntry) => teamEntry !== null && typeof teamEntry === 'object'
  )
  const resultsT = await getData('tournaments', 'id', id)
  if (resultsT.length === 0) throw new Error('No tournament data found')

  const { value: tournamentValue } = resultsT[0]
  const isFinish = tournamentValue.status_cup === 'Турнир завершен'
  const allLogo = Object.values(tournamentValue.sponsor || {})
  const administrator = Object.values(tournamentValue.administrator || [])

  const getTeamName = (teamData) => {
    return resultsT[0].competition_type === 'Рейтинговое соревнование'
      ? teamData?.team_name_ru
      : teamData?.preferred_name === 'en'
        ? teamData?.team_name_en || teamData?.team_name_ru
        : teamData?.team_name_ru
  }
  console.log('stageArray', stageArray)
  console.log('filteredTeams', filteredTeams)
  // Формируем пары
  const pairs = filteredTeams.map((team) => {
    const athletes = team.athlete || []
    // Собираем все записи fishList для данной команды
    const allFishLists = stageArray[0]
      .filter((e) => e.teamId === team.team.id)
      .flatMap((e) => e.fishList || [])

    return {
      id: team.team.id,
      teamName: getTeamName(team.team),
      sp: athletes
        .map(
          (a) =>
            `${a.fio || ''}${a.sports_category ? ` (${a.sports_category})` : ''}`
        )
        .join(', '),
      sector: team.team.sector,
      fishList: allFishLists, // Теперь содержит все рыбы из всех взвешиваний
      disqualified: team.team.status === 'Снята',
    }
  })
  // Рассчитываем ранги
  const rankedPairs = calculateRankings(pairs)

  // Находим самую крупную рыбу
  const biggestFish = rankedPairs.reduce(
    (max, pair) => {
      const pairBiggest = Math.max(
        ...pair.fishList.map((f) => f.weight || 0),
        0
      )
      return pairBiggest > max.weight
        ? { weight: pairBiggest, pair: pair.teamName }
        : max
    },
    { weight: 0, pair: '' }
  )

  // Общие показатели
  const totalFishCount = rankedPairs.reduce(
    (sum, p) => sum + p.fishList.filter((f) => f.weight > 0).length,
    0
  )
  const totalWeight = rankedPairs.reduce(
    (sum, p) => sum + p.fishList.reduce((s, f) => s + (f.weight || 0), 0),
    0
  )
  const averageFishWeight =
    totalFishCount > 0 ? Math.round(totalWeight / totalFishCount) : 0

  return {
    allLogo,
    mainLogo: getLogoUrl(tournamentValue?.logocup),
    data: rankedPairs,
    administrator,
    tournament: tournamentValue,
    date: dateNowProtocol(new Date()),
    totalFishCount,
    totalWeight,
    averageFishWeight,
    biggestFish,
    isFinish,
  }
}

const uploadPdfToServer = async (blob, id) => {
  const formData = new FormData()
  formData.append(
    'pdf',
    blob,
    `количество_протокол_${dateNowProtocol(new Date())}.pdf`
  )
  formData.append('id', id)
  formData.append('isPost', 3)
  formData.append('type', 'ProtocolСount')

  try {
    const response = await fetch(
      'https://www.admin-protocarp.ru/php/uploads/upload.php',
      {
        method: 'POST',
        body: formData,
      }
    )
    return response.ok ? await response.json() : null
  } catch (error) {
    console.error('Upload error:', error)
    return null
  }
}

export default {
  generate: async function (id) {
    try {
      const protocolData = await generateProtocolData(id)
      const pdfDoc = <PairCountTournamentProtocolPDF {...protocolData} />

      const blob = await pdf(pdfDoc).toBlob()
      const uploadResult = await uploadPdfToServer(blob, id)

      return {
        blob,
        previewUrl: uploadResult || URL.createObjectURL(blob),
        data: protocolData,
      }
    } catch (error) {
      console.error('Generation error:', error)
      throw error
    }
  },
}
